import { ReadOnlyField } from "@/controls/fields/ReadOnlyField";
import { ResetFiltersButton } from "@/controls/buttons/ResetFiltersButton";
import { useCallback, useRef, useState } from "react";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import { ExcelButton } from "@/controls/buttons/ExcelButton";
import { PdfButton } from "@/controls/buttons/PdfButton";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	IRowNode,
	RowClassParams,
} from "ag-grid-community";
import { ErrorModal } from "@dexteel/mesf-core";
import { useRollsInUse } from "../hooks/useRollsInUse";
import { useStands } from "@/shared/hooks/useStands";
import { StandSelector } from "@/shared/selectors/StandSelector";
import type { RollInUseRow } from "../models/RollsInUse";
import moment from "moment";
import { useReportPdfExport } from "@/shared/hooks/useReportPdfExport";
import { getReportPdf } from "@/shared/repositories/ReportPdfRepository";
import { gridToPdfTable } from "@/shared/utils/gridToPdfTable";

const num = (dec: number) => (p: { value: unknown }) =>
	p.value == null || p.value === "" ? "" : Number(p.value).toFixed(dec);

// The RollShop print lists each rollset as a block: its stand, mill size, barrel and name
// on the first roll, a rule above it. Read from the displayed order, so a sort keeps it.
type RowParams = {
	node: IRowNode<RollInUseRow> | null;
	api: GridApi<RollInUseRow>;
	data?: RollInUseRow;
};
const opensRollset = ({ node, api, data }: RowParams) => {
	const index = node?.rowIndex;
	if (index == null || index === 0) return true;
	return api.getDisplayedRowAtIndex(index - 1)?.data?.Rollset !== data?.Rollset;
};
const ROLLSET_COLUMNS = [
	"StandName",
	"MillSize",
	"BarrelDescription",
	"Rollset",
];
const rollsetCell = (p: ICellRendererParams<RollInUseRow>) =>
	opensRollset(p) ? (p.valueFormatted ?? p.value) : "";
const ROLLSET_RULE = { borderTop: "1px solid #9e9e9e" };
const getRowStyle = (p: RowClassParams<RollInUseRow>) =>
	p.node.rowIndex !== 0 && opensRollset(p) ? ROLLSET_RULE : undefined;

const RIGHT = { textAlign: "right" } as const;

// Columns in the order of the RollShop print.
const COLUMN_DEFS: ColDef<RollInUseRow>[] = [
	{
		field: "StandName",
		headerName: "Stand",
		flex: 0.6,
		minWidth: 70,
		cellRenderer: rollsetCell,
	},
	{
		field: "MillSize",
		headerName: "Mill Size",
		flex: 0.6,
		minWidth: 80,
		cellRenderer: rollsetCell,
	},
	{
		field: "BarrelDescription",
		headerName: "Barrel",
		flex: 2,
		minWidth: 200,
		cellRenderer: rollsetCell,
	},
	{
		field: "Rollset",
		headerName: "Rollset",
		flex: 1,
		minWidth: 120,
		cellRenderer: rollsetCell,
	},
	{ field: "Position", headerName: "Position", flex: 0.6, minWidth: 75 },
	{ field: "RollMaterial", headerName: "Material", flex: 1.2, minWidth: 140 },
	{ field: "Hardness", headerName: "Hardness", flex: 0.6, minWidth: 85 },
	{ field: "RollNumber", headerName: "Roll Number", flex: 0.8, minWidth: 100 },
	{ field: "Name", headerName: "Foundry Number", flex: 1, minWidth: 120 },
	{
		field: "Diameter",
		headerName: "Diameter",
		flex: 0.7,
		minWidth: 90,
		cellStyle: RIGHT,
		valueFormatter: num(3),
	},
	{
		field: "CurrentPercentage",
		headerName: "Current %",
		flex: 0.7,
		minWidth: 90,
		cellStyle: RIGHT,
		valueFormatter: num(2),
	},
	{
		field: "CurrentValue",
		headerName: "Current Value",
		flex: 0.8,
		minWidth: 105,
		cellStyle: RIGHT,
		valueFormatter: num(2),
	},
];

// The legacy screen carried the stand in its URL as "stand".
const URL_SCHEMA = { stand: urlParam.int() };

export const RollsInUseReport = () => {
	const [
		{ stand: standId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<RollInUseRow> | null>(null);

	const dataQuery = useRollsInUse(standId);
	// The legacy screen reads CAT.GetStands directly, straighteners included.
	const standsQuery = useStands({ includeStraighteners: true });
	useDropUnknownValue(
		standId,
		standsQuery.isSuccess,
		(standsQuery.data ?? []).map((s) => s.Id),
		useCallback(() => dropValues("stand"), [dropValues]),
	);
	const rows = dataQuery.data ?? [];
	const selectedStand =
		(standsQuery.data ?? []).find((s) => s.Id === standId) ?? null;

	const handleGridReady = useCallback((e: GridReadyEvent<RollInUseRow>) => {
		gridApiRef.current = e.api;
	}, []);

	const handleExportExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({ fileName: "Rolls_In_Use.xlsx" });
	}, []);

	const { exporting: exportingPdf, exportPdf } =
		useReportPdfExport(setErrorMsg);
	const handleExportPdf = () => {
		const api = gridApiRef.current;
		if (!api) return;
		const stand = selectedStand?.Name ?? "All";
		const table = gridToPdfTable(api, {
			countLabel: "Total Rolls",
			aligns: {
				BarrelDescription: "left",
				RollMaterial: "left",
				Diameter: "right",
				CurrentPercentage: "right",
				CurrentValue: "right",
			},
			blocks: { key: "Rollset", repeat: ROLLSET_COLUMNS },
		});
		exportPdf(
			`Rolls in Use ${selectedStand?.Name ?? "All stands"} ${moment().format("MM-DD-YYYY")}.pdf`,
			() =>
				getReportPdf({
					title: "Rolls in Use",
					filters: [{ label: "Stand", value: stand }],
					tables: [table],
				}),
		);
	};

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Rolls in Use
			</Typography>

			<Paper elevation={3}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 1,
						px: 1.5,
						py: 0.75,
						flexWrap: "wrap",
					}}
				>
					<StandSelector
						includeStraighteners
						shrinkLabel={false}
						value={standId}
						onChange={(id) => setFilters({ stand: id })}
						sx={{ minWidth: 220 }}
					/>
					<ResetFiltersButton
						onClick={() => resetValues()}
						disabled={isDefault()}
					/>
					<ReadOnlyField label="Rolls" value={rows.length} width={80} />
					<Box sx={{ flexGrow: 1, minWidth: 8 }} />

					<Box sx={{ display: "flex", gap: 1 }}>
						<ExcelButton handleExportToExcel={handleExportExcel} />
						<PdfButton
							handleExportToPdf={handleExportPdf}
							disabled={dataQuery.isLoading || rows.length === 0}
							loading={exportingPdf}
						/>
					</Box>
				</Box>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden" }}>
				{dataQuery.isLoading ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<AgGridReact<RollInUseRow>
						rowData={rows}
						columnDefs={COLUMN_DEFS}
						defaultColDef={{ resizable: true, sortable: true }}
						suppressContextMenu
						suppressCellFocus
						headerHeight={32}
						rowHeight={28}
						getRowStyle={getRowStyle}
						onSortChanged={(e) => e.api.redrawRows()}
						onGridReady={handleGridReady}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
